import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, XCircle, Clock, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import JourneySectionHeader from "@/components/JourneySectionHeader";
import { JourneyProgressSection } from "@/apps/los/components/ProgressBar/JourneySectionProgress";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface CaprionAgreementStatusProps {
    isBuilderMode?: boolean;
    onDone?: () => void;
    /** Passed by JourneyPage to every fullPageComponent — drives the header progress bar. */
    progressSections?: JourneyProgressSection[];
    currentPageId?: number | null;
}

const CaprionAgreementStatus: React.FC<CaprionAgreementStatusProps> = ({
    isBuilderMode = false,
    onDone,
    progressSections,
    currentPageId,
}) => {
    const [loading, setLoading] = useState(true);
    const [agreementStatus, setAgreementStatus] = useState<string>('');
    const [error, setError] = useState<string>('');
    /**
     * True when this borrower's dealer approval was held back to the early hours
     * (they reached the previous page late at night — see
     * los_app/services/dealer_approval_defer.py). The lender has not been asked
     * to approve yet, so there is no agreement status to show and the normal
     * screen would look like a failure. Flagged by the backend, which decides
     * this from the DeferredDealerApproval row rather than trusting the client.
     */
    const [deferred, setDeferred] = useState(false);

    useEffect(() => {
        if (isBuilderMode) {
            setLoading(false);
            return;
        }

        fetchAgreementStatus();
    }, [isBuilderMode]);

    const fetchAgreementStatus = async () => {
        setLoading(true);
        setError('');

        try {
            const response = await axios.get(`${API_BASE_URL}/api/caprion/get-agreement-status/`);
            
            if (response.data.deferred) {
                // Terminal state for tonight: nothing to poll, nothing for the
                // borrower to do, and deliberately NO auto-advance — this is the
                // last thing they should see.
                setDeferred(true);
                setLoading(false);
                return;
            }

            if (response.data.success) {
                const status = response.data.agreement_status || response.data.data?.agreement_status;
                setAgreementStatus(status);
                
                // Auto-advance after showing status for 2 seconds
                if (status && onDone) {
                    setTimeout(() => {
                        onDone();
                    }, 2000);
                }
            } else {
                setError(response.data.error || 'Failed to fetch agreement status');
                toast.error('Failed to fetch agreement status');
            }
        } catch (err: any) {
            console.error('Error fetching agreement status:', err);
            const errorMsg = err.response?.data?.error || err.message || 'Failed to fetch agreement status';
            setError(errorMsg);
            toast.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const getStatusIcon = () => {
        if (loading) {
            return <Loader2 className="h-16 w-16 text-blue-500 animate-spin" />;
        }

        switch (agreementStatus?.toLowerCase()) {
            case 'accepted':
            case 'signed':
                return <CheckCircle className="h-16 w-16 text-intellend-green" />;
            case 'rejected':
            case 'failed':
                return <XCircle className="h-16 w-16 text-red-500" />;
            case 'pending':
            case 'in_progress':
                return <Clock className="h-16 w-16 text-yellow-500" />;
            default:
                return <Clock className="h-16 w-16 text-blue-500" />;
        }
    };

    const getStatusColor = () => {
        switch (agreementStatus?.toLowerCase()) {
            case 'accepted':
            case 'signed':
                return 'bg-intellend-green/5 border-intellend-green/20';
            case 'rejected':
            case 'failed':
                return 'bg-red-50 border-red-200';
            case 'pending':
            case 'in_progress':
                return 'bg-yellow-50 border-yellow-200';
            default:
                return 'bg-blue-50 border-blue-200';
        }
    };

    const getStatusMessage = () => {
        if (loading) return 'Checking agreement status...';
        if (error) return error;

        switch (agreementStatus?.toLowerCase()) {
            case 'accepted':
            case 'signed':
                return 'Your loan agreement has been successfully signed!';
            case 'rejected':
            case 'failed':
                return 'Agreement signing failed. Please contact support.';
            case 'pending':
            case 'in_progress':
                return 'Agreement signing is in progress...';
            default:
                return agreementStatus || 'Status unknown';
        }
    };

    // Builder mode placeholder
    if (isBuilderMode) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
                <div className="max-w-2xl mx-auto">
                    <Card>
                        <CardHeader>
                            <CardTitle>Agreement Status</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-muted-foreground">
                                This page will automatically check and display the agreement signing status.
                            </p>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    // Held-back approval: a dedicated terminal screen. Shown ONLY to borrowers
    // whose dealer approval went into the overnight queue; everyone else falls
    // through to the normal status screen below, unchanged.
    //
    // Deliberately does not claim the loan is approved — the lender has not been
    // asked yet — but also does not look like an error, because nothing has gone
    // wrong and there is nothing for the borrower to do.
    if (deferred) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col">
                <JourneySectionHeader
                    className="shrink-0"
                    title="Application Complete"
                    progressSections={progressSections}
                    currentPageId={currentPageId}
                />

                <div className="flex-1 flex items-center justify-center p-4">
                    <Card className="w-full max-w-2xl border-2 bg-intellend-green/5 border-intellend-green/20">
                        <CardHeader>
                            <CardTitle className="text-center text-2xl">
                                Application Submitted
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="flex justify-center">
                                <CheckCircle className="h-16 w-16 text-intellend-green" />
                            </div>

                            <div className="space-y-3 text-center">
                                <p className="text-lg font-semibold text-gray-900">
                                    Thank you! Your application is complete.
                                </p>
                                <p className="text-sm text-gray-700">
                                    Your loan is being processed tonight. You will
                                    receive a confirmation once it is done, and the
                                    amount will be credited to your bank account.
                                </p>
                                <p className="text-sm text-gray-600">
                                    You do not need to do anything else.
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col">
            {/* Header — page title + journey progress, same as every other page */}
            <JourneySectionHeader
                className="shrink-0"
                title="Application Complete"
                progressSections={progressSections}
                currentPageId={currentPageId}
            />

            {/* Main Content */}
            <div className="flex-1 flex items-center justify-center p-4">
                <Card className={`w-full max-w-2xl border-2 ${getStatusColor()}`}>
                    <CardHeader>
                        <CardTitle className="text-center text-2xl">Agreement Status</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* Status Icon */}
                        <div className="flex justify-center">
                            {getStatusIcon()}
                        </div>

                        {/* Status Message */}
                        <div className="text-center">
                            <p className="text-lg font-semibold text-gray-900 mb-2">
                                {agreementStatus || 'Checking...'}
                            </p>
                            <p className="text-gray-600">
                                {getStatusMessage()}
                            </p>
                        </div>

                        {/* Additional Info */}
                        {agreementStatus?.toLowerCase() === 'accepted' && (
                            <div className="bg-intellend-green/5 border border-intellend-green/20 rounded-lg p-4">
                                <p className="text-sm text-intellend-green text-center">
                                    ✓ Your loan application is now complete. Your loan will be disbursed shortly.
                                </p>
                            </div>
                        )}

                        {error && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                <p className="text-sm text-red-800 text-center">
                                    {error}
                                </p>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default CaprionAgreementStatus;
